/**
 * The zero-results page. (item 18)
 *
 * Core's is a single sentence — "No results found." — which is the least useful
 * thing an archive can say. In a corpus this shaped, an empty result set is
 * almost never "we don't have it": it is a filter the user didn't set, a scope
 * they didn't know about, or a document number typed with the wrong separator.
 * The seam already reports which of those applies (`search_meta`, including a
 * `*_defaulted` flag per knob), so this panel names the specific reason and
 * gives a one-click way out of it — ordered by how likely it is to be the cause.
 *
 * Everything here is derived, never guessed: an escape hatch is offered ONLY
 * when the corresponding constraint is actually in force. A search that already
 * spans every archive, every status and every word is offered no false hope —
 * it gets the honest advice instead.
 *
 * Rendered through the `cards-id` listing variation's `noResultsComponent`, so
 * browse listings get it too; there the seam answers from the stock catalog and
 * `meta` is null, which degrades to the plain message plus the generic tips.
 */
import React from 'react';
import { Link } from 'react-router-dom';

import { stripPrefixes, aliasNoun } from '../../helpers/queryFields';
import { contactHref } from '../ContactForm/categories';
import useSearchMeta from '../../helpers/useSearchMeta';
import useSearchControls from '../../helpers/useSearchControls';
import useSiteConfig from '../../helpers/useSiteConfig';
import {
  SITE_CHOICES,
  STATUS_GROUPS,
} from '../../config/searchRegistry.generated';

const ALL_SITES = SITE_CHOICES.map((s) => s.value);

const statusLabel = (id) => STATUS_GROUPS.find((g) => g.id === id)?.label || id;

const SearchNoResults = (props) => {
  const { isEditMode, block, onSetSites, onSetSearchText } = props;
  const { meta } = useSearchMeta(block);
  const [, setControls] = useSearchControls();
  const site = useSiteConfig();

  const term = meta?.term || '';
  const scopeSites = Array.isArray(meta?.scope)
    ? meta.scope.filter(Boolean)
    : [];
  const otherArchives = SITE_CHOICES.filter((s) => s.value !== site?.id);

  const fixes = [];
  // A field prefix (item 4) goes first when there is one: it is the narrowest
  // thing the user can have done and the easiest to have done by accident —
  // `title:welding` finding nothing says something quite different from the
  // archive having nothing about welding, and only this line can tell them
  // which happened.
  const fielded = Array.isArray(meta?.fielded) ? meta.fielded : [];
  if (fielded.length && term && onSetSearchText) {
    const widened = stripPrefixes(term);
    fixes.push({
      key: 'fielded',
      text: `You're only searching ${fielded
        .map(aliasNoun)
        .join(' and ')}.`,
      action: 'Search everywhere instead',
      onClick: () => onSetSearchText(widened),
    });
  }
  // Then, ordered by how often each is the real cause. A status filter
  // silently hides ~two thirds of the corpus, so it leads whenever it is on.
  if (meta && meta.status_group && meta.status_group !== 'All') {
    fixes.push({
      key: 'status',
      text: `You're only seeing ${statusLabel(meta.status_group).toLowerCase()} documents.`,
      action: 'Search all documents',
      onClick: () => setControls({ status_group: 'All' }),
    });
  }
  if (meta && meta.operator === 'AND') {
    fixes.push({
      key: 'operator',
      text: 'Every word you typed has to appear in the document.',
      action: 'Match any word instead',
      onClick: () => setControls({ operator: 'OR' }),
    });
  }
  // Scope is a FACET, not a URL control: core seeds facet state from a simple
  // `?site=` param only on the block's first render (and wraps a multi-valued
  // one in a second array), so writing the URL here would do nothing on the
  // already-mounted page this panel lives in. The search block hands down a
  // callback that goes through its own facet state instead.
  if (meta && scopeSites.length === 1 && otherArchives.length && onSetSites) {
    fixes.push({
      key: 'scope',
      text: `You're searching ${site?.title || 'this archive'} only.`,
      action: `Search all ${SITE_CHOICES.length} archives`,
      onClick: () => onSetSites(ALL_SITES),
    });
  }

  return (
    <div className="search-noresults">
      <h2 className="search-noresults__title">
        {term ? (
          <>
            No documents match{' '}
            <span className="search-noresults__term">{term}</span>
          </>
        ) : (
          'No documents match these filters'
        )}
      </h2>

      {fixes.length > 0 && (
        <ul className="search-noresults__fixes">
          {fixes.map((f) => (
            <li key={f.key}>
              <span className="search-noresults__reason">{f.text}</span>{' '}
              <button
                type="button"
                className="search-noresults__action"
                onClick={() => !isEditMode && f.onClick()}
              >
                {f.action}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="search-noresults__tips">
        <h3>Other things to try</h3>
        <ul>
          <li>
            Search the document number on its own — <code>413.3B</code> finds it
            however it is punctuated.
          </li>
          <li>Use fewer, more distinctive words.</li>
          <li>
            Clear any filters in the left column — each one narrows the set.
          </li>
          {term && (
            <li>
              Check the spelling; the archive matches the text inside every PDF,
              so a correctly spelled phrase from the document will find it.
            </li>
          )}
          {/* BL-0096. A search that found nothing is the archive's most
              honest signal that something is missing or mis-indexed, and it
              is worthless unless the person can say so from here. The query
              rides along, so the report arrives with the exact term that
              failed rather than a recollection of it. */}
          {!isEditMode && (
            <li>
              Still nothing?{' '}
              <Link
                rel="nofollow"
                to={contactHref({ topic: 'search-problem', q: term })}
              >
                Tell us what you were looking for
              </Link>{' '}
              — we read every one.
            </li>
          )}
        </ul>
      </div>

      {!isEditMode && (
        <p className="search-noresults__elsewhere">
          Browse instead: <Link to="/">{site?.title || 'Home'}</Link>
          {onSetSites && otherArchives.length > 0 && (
            <>
              {' · '}
              {otherArchives.map((s, i) => (
                <React.Fragment key={s.value}>
                  {i > 0 && ' · '}
                  <button
                    type="button"
                    className="search-noresults__linklike"
                    onClick={() => onSetSites([s.value])}
                  >
                    Search {s.label}
                  </button>
                </React.Fragment>
              ))}
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default SearchNoResults;
